import type { SeriesTemplateAttributes, SessionTemplateAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import SeriesTemplateQ from './SeriesTemplateQ'
import TopicQ from './TopicQ'
import UserQ from './UserQ'
import WorldQ from './WorldQ'

const SessionTemplateQ = {
  ...makeQueryModelV2<SessionTemplateAttributes>('session_template')([
    'id',
    'title',
    'description',
    'external_description',
    'questionnaire',
    'curriculum',
    'max_learners',
    'allow_cohosts',
    'topic_id',
    'series_template_id',
    'start_date',
    'duration_minutes',
    'user_id',
    'world_id',
    'active',
    'series_position',
    'attendance_policy',
    'is_breakout_enabled',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    World: {
      query: WorldQ,
      foreignKey: WorldQ.id,
      sourceKey: SessionTemplateQ.world_id,
    },
    SeriesTemplate: {
      query: SeriesTemplateQ,
      foreignKey: SeriesTemplateQ.id,
      sourceKey: SessionTemplateQ.series_template_id,
    },
    User: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: SessionTemplateQ.user_id,
    },
    Topic: {
      query: TopicQ,
      foreignKey: TopicQ.id,
      sourceKey: SessionTemplateQ.topic_id,
    },
  }),

  canUserTutor: ({ userId }: { userId: number | SQLFragment<number> }): SQLFragment<boolean> =>
    TopicQ.canUserTutor(userId),

  countSessionTemplatesForSeriesTemplate: ({
    seriesTemplateId,
  }: {
    seriesTemplateId: number | SQLFragment<number>
  }): SQLFragment<number> => SQL`(SELECT COUNT(*) ${SessionTemplateQ.where({ seriesTemplateId })})`,

  where: (
    {
      paranoid = true,
      id,
      seriesTemplateId,
      userId,
      worldId,
      active,
      include = [],
      groupBy = null,
      order = null,
      limit = null,
    }: {
      paranoid?: boolean
      id?: number | null | SQLFragment<number>
      seriesTemplateId?: number | null | SQLFragment<number>
      userId?: number | null | SQLFragment<number>
      active?: boolean | null
      worldId?: number | null | SQLFragment<number>
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      limit?: number | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<SeriesTemplateAttributes> => SQL`
        FROM ${SessionTemplateQ}
        ${includeAssociationsToSql(SessionTemplateQ, include)}
        WHERE
            ${id != null ? SQL`${SessionTemplateQ.id} = ${id} AND` : SQL``}
            ${
              seriesTemplateId != null
                ? SQL`${SessionTemplateQ.series_template_id} = ${seriesTemplateId} AND`
                : SQL``
            }
            ${userId != null ? SQL`${SessionTemplateQ.user_id} = ${userId} AND` : SQL``}
            ${active != null ? SQL`${SessionTemplateQ.active} = ${active} AND` : SQL``}
            ${worldId != null ? SQL`${SessionTemplateQ.world_id} = ${worldId} AND` : SQL``}
            ${paranoid ? SQL`${SessionTemplateQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
        ${queryLimitToSQL(limit)}
    `,
}

export default SessionTemplateQ
